package com.vizio.redwolf.auth.api

import com.vizio.redwolf.auth.client.PinHttpClient
import com.vizio.redwolf.auth.config.PinApiJsonConfig
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PurchasePinEnvironmentConfig
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.retryRequest
import io.ktor.client.call.body
import kotlinx.serialization.PolymorphicSerializer
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.decodeFromJsonElement

/**
 * Refer - https://vizio-sc.atlassian.net/l/c/6fQyBrbe
 */
class PurchasePinApi(pinEnv: PinEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS)) {

    private val pinClient = PinHttpClient(pinEnv = pinEnv, appConfig = appConfig, clientConfig = clientConfig)

    constructor(envConfig: PurchasePinEnvironmentConfig, appConfig: AppConfig) : this(choosePinEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS))
    constructor(envConfig: PurchasePinEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) : this(choosePinEnvironment(envConfig), appConfig, clientConfig)
    constructor(envConfig: PurchasePinEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(choosePinEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.SDK_HEADERS, timeout))

    /**
     * Check if given account with access token has pin setup
     */
    suspend fun hasPin(hasPinInfo: HasPinInfo, sentryHeader: String? = null, traceId: String? = null): HasPinResult {
        return retryPurchasePinRequest {
            when (val result = pinClient.hasPin(hasPinInfo, sentryHeader, traceId)) {
                is HttpResult.Success -> {
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    try {
                        val successResponse = result.data.body<JsonElement>()
                        var hasPinResponse = PinApiJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(HasPinResponse::class), successResponse)
                        if (hasPinResponse is HasPinResponse.BadPayload) {
                            hasPinResponse = Json.decodeFromJsonElement<HasPinResponse.Unauthorized>(successResponse)
                        }
                        HasPinResult(headers, hasPinResponse)
                    } catch (exception: SerializationException) {
                        HasPinResult(headers, HasPinResponse.Error(exception))
                    }
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        HasPinResult(headers, HasPinResponse.Timeout(exception))
                    }
                    else {
                        HasPinResult(headers, HasPinResponse.Error(exception))
                    }
                }
            }
        }
    }

    /**
     * Create Pin for a given access token
     */
    suspend fun createPin(createPinInfo: CreatePinInfo, sentryHeader: String? = null, traceId: String? = null): CreatePinResult {
        return retryPurchasePinRequest {
            when (val result = pinClient.createPin(createPinInfo, sentryHeader, traceId)) {
                is HttpResult.Success -> {
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    try {
                        val successResponse = result.data.body<JsonElement>()
                        var createPinResponse = PinApiJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(CreatePinResponse::class), successResponse)
                        if (createPinResponse is CreatePinResponse.BadPayload) {
                            createPinResponse = Json.decodeFromJsonElement<CreatePinResponse.Unauthorized>(successResponse)
                        }
                        CreatePinResult(headers, createPinResponse)
                    } catch (exception: SerializationException) {
                        CreatePinResult(headers, CreatePinResponse.Error(exception))
                    }
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        CreatePinResult(headers, CreatePinResponse.Timeout(exception))
                    }
                    else {
                        CreatePinResult(headers, CreatePinResponse.Error(exception))
                    }
                }
            }
        }
    }

    /**
     * Validate Pin for a given access token
     */
    suspend fun validatePin(validatePinInfo: ValidatePinInfo, sentryHeader: String? = null, traceId: String? = null): ValidatePinResult {
        return retryPurchasePinRequest {
            when (val result = pinClient.validatePin(validatePinInfo, sentryHeader, traceId)) {
                is HttpResult.Success -> {
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    try {
                        val successResponse = result.data.body<JsonElement>()
                        var validatePinResponse = PinApiJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(ValidatePinResponse::class), successResponse)
                        if (validatePinResponse is ValidatePinResponse.BadPayload) {
                            validatePinResponse = Json.decodeFromJsonElement<ValidatePinResponse.Unauthorized>(successResponse)
                        }
                        ValidatePinResult(headers, validatePinResponse)
                    } catch (exception: SerializationException) {
                        ValidatePinResult(headers, ValidatePinResponse.Error(exception))
                    }
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        ValidatePinResult(headers, ValidatePinResponse.Timeout(exception))
                    }
                    else {
                        ValidatePinResult(headers, ValidatePinResponse.Error(exception))

                    }
                }
            }
        }
    }

    /**
     * Update Pin for a given access token
     */
    suspend fun updatePin(updatePinInfo: UpdatePinInfo, sentryHeader: String? = null, traceId: String? = null): UpdatePinResult {
        return retryPurchasePinRequest {
            when (val result = pinClient.updatePin(updatePinInfo, sentryHeader, traceId)) {
                is HttpResult.Success -> {
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    try {
                        val successResponse = result.data.body<JsonElement>()
                        var updatePinResponse = PinApiJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(UpdatePinResponse::class), successResponse)
                        if (updatePinResponse is UpdatePinResponse.BadPayload) {
                            updatePinResponse = Json.decodeFromJsonElement<UpdatePinResponse.Unauthorized>(successResponse)
                        }
                        UpdatePinResult(headers, updatePinResponse)
                    } catch (exception: SerializationException) {
                        UpdatePinResult(headers, UpdatePinResponse.Error(exception))
                    }
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        UpdatePinResult(headers, UpdatePinResponse.Timeout(exception))
                    }
                    else {
                        UpdatePinResult(ResponseHeaders.Invalid, UpdatePinResponse.Error(exception))
                    }
                }
            }
        }
    }

    /**
     * Delete Pin for a given access token
     */
    suspend fun deletePin(deletePinInfo: DeletePinInfo, sentryHeader: String? = null, traceId: String? = null): DeletePinResult {
        return retryPurchasePinRequest {
            when (val result = pinClient.deletePin(deletePinInfo, sentryHeader, traceId)) {
                is HttpResult.Success -> {
                    val statusCode = result.data.status.value
                    val headers = ResponseHeaders.Valid(statusCode)
                    try {
                        val successResponse = result.data.body<JsonElement>()
                        var deletePinResponse = PinApiJsonConfig.config.decodeFromJsonElement(PolymorphicSerializer(DeletePinResponse::class), successResponse)
                        if (deletePinResponse is DeletePinResponse.BadPayload) {
                            deletePinResponse = Json.decodeFromJsonElement<DeletePinResponse.Unauthorized>(successResponse)
                        }
                        DeletePinResult(headers, deletePinResponse)
                    } catch (exception: SerializationException) {
                        DeletePinResult(headers, DeletePinResponse.Error(exception))
                    }
                }

                is HttpResult.Error -> {
                    val headers = ResponseHeaders.Invalid
                    val exception = result.exception
                    if (exception is TimeoutException) {
                        DeletePinResult(headers, DeletePinResponse.Timeout(exception))
                    }
                    else {
                        DeletePinResult(headers, DeletePinResponse.Error(exception))
                    }
                }
            }
        }
    }

    private fun <T : Any> isPinSuccess(result: T): Boolean {
        return if (result is HasPinResult && result.response is HasPinResponse.PinFound)
            true
        else if (result is CreatePinResult && result.response is CreatePinResponse.Success)
            true
        else if (result is UpdatePinResult && result.response is UpdatePinResponse.Success)
            true
        else if (result is ValidatePinResult && result.response is ValidatePinResponse.ValidPin)
            true
        else if (result is DeletePinResult && result.response is DeletePinResponse.Success)
            true
        else result is VersionResult<*, *> && result is VersionResult.Success<*, *>
    }

    private suspend inline fun <T : Any> retryPurchasePinRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isPinSuccess)
    }

}
